import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Snackbar, Typography
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import apiService from '../../services/apiService';
import PlaybookDetectionSettings from './PlaybookDetectionSettings';
import PlaybookItemEditor from './PlaybookItemEditor';
import {
  DiscoveryPlaybook,
  PlaybookCategory,
  PlaybookDetection,
  PlaybookItem
} from '../../types/discoveryPlaybook';

/** Tier 1 is the "at any cost" set — the same four the weight editor protects. */
const TIER_LABELS: Record<number, string> = {
  1: 'Tier 1 · at any cost',
  2: 'Tier 2',
  3: 'Tier 3'
};

/**
 * Knowledge Base > Discovery Playbook.
 *
 * Edits the coaching content the live checklist runs on: 10 categories, 33
 * moves, mirroring the post-call rubric one-for-one. Items cannot be added or
 * removed here — the set is fixed against two source documents and joined to
 * the rubric by `rubricAspect`. Re-seeding is the supported path for structural
 * change (`backend/src/scripts/seedDiscoveryPlaybook.js`).
 *
 * Saving bumps the playbook version so every session records which revision it
 * was coached against.
 */
const DiscoveryPlaybookTab: React.FC = () => {
  const [playbook, setPlaybook] = useState<DiscoveryPlaybook | null>(null);
  const [original, setOriginal] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.get<DiscoveryPlaybook>('/playbook');
        if (cancelled) return;
        const loaded = response?.data ?? null;
        setPlaybook(loaded);
        setOriginal(JSON.stringify(loaded ?? {}));
      } catch (e: any) {
        if (cancelled) return;
        // A tenant that has never been seeded is the expected 404 here, and the
        // fix is a script run rather than anything the admin can do in the UI.
        const notSeeded = e?.response?.data?.error === 'PLAYBOOK_NOT_SEEDED';
        setError(
          notSeeded
            ? 'The discovery playbook has not been set up for this account yet. Please contact support.'
            : e?.message || 'Could not load the discovery playbook. Please try again.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isDirty = useMemo(
    () => playbook !== null && JSON.stringify(playbook) !== original,
    [playbook, original]
  );

  const handleItemChange = useCallback((categoryKey: string, nextItem: PlaybookItem) => {
    setPlaybook((current) => {
      if (!current) return current;
      return {
        ...current,
        categories: current.categories.map((category) =>
          category.key !== categoryKey
            ? category
            : {
                ...category,
                items: category.items.map((item) =>
                  item.key === nextItem.key ? nextItem : item
                )
              }
        )
      };
    });
  }, []);

  const handleDetectionChange = useCallback((detection: PlaybookDetection) => {
    setPlaybook((current) => (current ? { ...current, detection } : current));
  }, []);

  const handleSave = async () => {
    if (!playbook || !isDirty) return;
    try {
      setSaving(true);
      setError(null);
      const response = await apiService.put<DiscoveryPlaybook>('/playbook', {
        categories: playbook.categories,
        detection: playbook.detection
      });
      const saved = response?.data ?? playbook;
      setPlaybook(saved);
      setOriginal(JSON.stringify(saved));
      setSavedToast(true);
    } catch (e: any) {
      setError(e?.message || 'Could not save your changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error && !playbook) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!playbook) {
    return <Alert severity="info">No discovery playbook is configured for this account.</Alert>;
  }

  return (
    <Box>
      <Box display="flex" alignItems="flex-start" gap={2} mb={2} flexWrap="wrap">
        <Box flexGrow={1}>
          <Typography variant="body2" color="text.secondary">
            The moves your reps are coached on live, and the boxes the post-call
            score ticks afterwards — the same 10 categories, seen from the other
            end. Editing the wording here changes what reps are prompted to say;
            it does not change how calls are graded.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip size="small" variant="outlined" label={`Version ${playbook?.version ?? 1}`} />
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={!isDirty || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <PlaybookDetectionSettings
        detection={playbook?.detection}
        onChange={handleDetectionChange}
      />

      {(playbook?.categories ?? []).map((category: PlaybookCategory) => (
        <Paper
          key={category?.key}
          elevation={0}
          sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={700}>
              {category?.number}. {category?.title}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              color={category?.tier === 1 ? 'primary' : 'default'}
              label={TIER_LABELS?.[category?.tier] ?? `Tier ${category?.tier}`}
            />
            <Box flexGrow={1} />
            <Typography variant="caption" color="text.secondary">
              {category?.items?.length ?? 0} items
            </Typography>
          </Box>

          {(category?.items ?? []).map((item: PlaybookItem) => (
            <PlaybookItemEditor
              key={item?.key}
              item={item}
              onChange={(nextItem) => handleItemChange(category.key, nextItem)}
            />
          ))}
        </Paper>
      ))}

      <Snackbar
        open={savedToast}
        autoHideDuration={3000}
        onClose={() => setSavedToast(false)}
        message="Discovery playbook saved"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default DiscoveryPlaybookTab;
